"use client";

import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useMedia } from "react-use";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import type { BikeClass } from "@/lib/mobility/bike-classes";
import {
  bikeTypeLabel,
  rideStyleLabel,
  type BikeType,
  type HubAxes,
  type PresentFacets,
  type RideStyle,
  type Transmission,
} from "@/lib/mobility/bike-facets";
import { bikeMakerLabel, type BikeMaker } from "@/lib/mobility/bike-makers";
import type { PartySize } from "@/lib/mobility/camp-party";
import { pickLabel, type HubTranslate } from "./hub-context";
import { FLOATING_SHADOW } from "./hub-ui";

type AxisKey = "maker" | "class" | "type" | "ride" | "trans" | "party";

/**
 * 알약 순서 = 제조사 · 배기량 · 타입 · 라이딩 · 변속 · 인원 (명소 알약은 그 앞, 허브가 붙인다).
 * 인원은 캠핑 전용이고 바이크 축은 모빌리티 전용이라 한 줄에 함께 나오지 않는다 —
 * 허브가 종류별로 반대쪽 값을 비워서 넘긴다.
 */
const AXIS_ORDER: readonly AxisKey[] = ["maker", "class", "type", "ride", "trans", "party"];

interface AxisPillsProps {
  axes: HubAxes;
  onChange: (axes: HubAxes) => void;
  /** 데이터셋에 실제로 있는 배기량 클래스(BIKE_CLASSES 순서). */
  classes: BikeClass[];
  /** 데이터셋에 실제로 있는 제조사·타입·라이딩·변속 값. */
  present: PresentFacets;
  /** 데이터셋에 실제로 있는 캠핑 인원(오름차순) — 비면 인원 알약을 숨긴다(모빌리티는 늘 빈 값). */
  parties?: readonly PartySize[];
  /** 지도 위에 띄울 때 — 링 + 그림자 + pointer-events-auto */
  floating?: boolean;
  locale: string;
  t: HubTranslate;
}

/**
 * 필터 축 알약 한 줄 — 축마다 알약 하나, 누르면 그 축의 피커가 열린다.
 * 피커 안의 선택은 **임시(draft)** 이고 푸터 "결과 보기"를 눌러야 반영된다(09-22 사용자
 * 결정). 바깥 탭·스와이프로 닫으면 버린다. 임시 선택은 알약 라벨에도 반영하지 않는다.
 *
 * 노출 규칙: 라이딩은 데이터에 `rideStyles` 가 하나도 없으면 아예 숨기고, 변속은
 * 값이 두 가지 모두 있을 때만(전부 AT 이면 고를 것이 없다), 제조사·타입·인원도 값이 있을 때만.
 * 배기량은 클래스가 하나라도 있으면 보인다.
 */
export function AxisPills({
  axes,
  onChange,
  classes,
  present,
  parties = [],
  floating = false,
  locale,
  t,
}: AxisPillsProps) {
  const [openAxis, setOpenAxis] = useState<AxisKey | null>(null);
  // 피커 안 임시 선택 — 열 때 현재 축을 복사하고, "결과 보기"에서만 onChange 로 올린다.
  const [draft, setDraft] = useState<HubAxes>(axes);
  const openPicker = (axis: AxisKey) => {
    setDraft(axes);
    setOpenAxis(axis);
  };
  const applyDraft = () => {
    onChange(draft);
    setOpenAxis(null);
  };
  const isWide = useMedia("(min-width: 640px)", false);

  const visible = AXIS_ORDER.filter((axis) => {
    switch (axis) {
      case "maker":
        return present.makers.length > 0;
      case "class":
        return classes.length > 0;
      case "type":
        return present.types.length > 0;
      case "ride":
        return present.rides.length > 0;
      case "trans":
        return present.trans.length >= 2;
      case "party":
        return parties.length > 0;
    }
  });
  if (visible.length === 0) return null;

  const partyLabel = (n: PartySize) => t("hub.camping.partyN", { n });

  const classLabel = (key: string) =>
    pickLabel(classes.find((c) => c.key === key)?.label, locale);

  /** 알약 라벨 — 선택 없으면 축 이름, 있으면 첫 값 + "+N". */
  const summaryOf = (axis: AxisKey): { label: string; extra: number } => {
    switch (axis) {
      case "maker":
        return axes.makers.length > 0
          ? {
              label: bikeMakerLabel(axes.makers[0]),
              extra: axes.makers.length - 1,
            }
          : { label: t("hub.axisMaker"), extra: 0 };
      case "class":
        return axes.classKey
          ? { label: classLabel(axes.classKey), extra: 0 }
          : { label: t("hub.axisClass"), extra: 0 };
      case "type":
        return axes.types.length > 0
          ? {
              label: bikeTypeLabel(axes.types[0], locale),
              extra: axes.types.length - 1,
            }
          : { label: t("hub.axisType"), extra: 0 };
      case "ride":
        return axes.rides.length > 0
          ? {
              label: rideStyleLabel(axes.rides[0], locale),
              extra: axes.rides.length - 1,
            }
          : { label: t("hub.axisRide"), extra: 0 };
      case "trans":
        return axes.trans
          ? { label: t(axes.trans === "AT" ? "hub.at" : "hub.mt"), extra: 0 }
          : { label: t("hub.axisTrans"), extra: 0 };
      case "party":
        return axes.parties.length > 0
          ? {
              label: partyLabel(axes.parties[0]),
              extra: axes.parties.length - 1,
            }
          : { label: t("hub.camping.axisParty"), extra: 0 };
    }
  };

  const titleOf = (axis: AxisKey) =>
    t(
      axis === "maker"
        ? "hub.axisMaker"
        : axis === "class"
        ? "hub.axisClass"
        : axis === "type"
        ? "hub.axisType"
        : axis === "ride"
        ? "hub.axisRide"
        : axis === "trans"
        ? "hub.axisTrans"
        : "hub.camping.axisParty",
    );

  const isActive = (axis: AxisKey) =>
    axis === "maker"
      ? axes.makers.length > 0
      : axis === "class"
      ? !!axes.classKey
      : axis === "type"
      ? axes.types.length > 0
      : axis === "ride"
      ? axes.rides.length > 0
      : axis === "trans"
      ? !!axes.trans
      : axes.parties.length > 0;

  const clearAxis = (axis: AxisKey) => {
    switch (axis) {
      case "maker":
        setDraft({ ...draft, makers: [] });
        break;
      case "class":
        setDraft({ ...draft, classKey: null });
        break;
      case "type":
        setDraft({ ...draft, types: [] });
        break;
      case "ride":
        setDraft({ ...draft, rides: [] });
        break;
      case "trans":
        setDraft({ ...draft, trans: null });
        break;
      case "party":
        setDraft({ ...draft, parties: [] });
        break;
    }
  };

  /** 피커 안 "선택 해제" 활성 여부 — 임시 선택 기준 */
  const draftActive = (axis: AxisKey): boolean => {
    switch (axis) {
      case "maker":
        return draft.makers.length > 0;
      case "class":
        return !!draft.classKey;
      case "type":
        return draft.types.length > 0;
      case "ride":
        return draft.rides.length > 0;
      case "trans":
        return !!draft.trans;
      case "party":
        return draft.parties.length > 0;
    }
  };

  const toggleMaker = (key: BikeMaker) =>
    setDraft({
      ...draft,
      makers: draft.makers.includes(key)
        ? draft.makers.filter((k) => k !== key)
        : [...draft.makers, key],
    });

  const toggleType = (key: BikeType) =>
    setDraft({
      ...draft,
      types: draft.types.includes(key)
        ? draft.types.filter((k) => k !== key)
        : [...draft.types, key],
    });

  const toggleRide = (key: RideStyle) =>
    setDraft({
      ...draft,
      rides: draft.rides.includes(key)
        ? draft.rides.filter((k) => k !== key)
        : [...draft.rides, key],
    });

  const toggleParty = (key: PartySize) =>
    setDraft({
      ...draft,
      parties: draft.parties.includes(key)
        ? draft.parties.filter((k) => k !== key)
        : [...draft.parties, key],
    });

  const setTrans = (key: Transmission) =>
    setDraft({ ...draft, trans: draft.trans === key ? null : key });

  const setClass = (key: string | null) => setDraft({ ...draft, classKey: key });

  const renderBody = (axis: AxisKey) => {
    switch (axis) {
      case "maker":
        // 타입과 같은 2열 타일 — 브랜드명은 모든 언어에서 라틴 표기, 보조 문구 없음.
        return (
          <div className="grid grid-cols-2 gap-2">
            {present.makers.map((key) => (
              <Tile
                key={key}
                label={bikeMakerLabel(key)}
                selected={draft.makers.includes(key)}
                onClick={() => toggleMaker(key)}
              />
            ))}
          </div>
        );
      case "class":
        return (
          <div className="flex flex-wrap gap-2">
            <OptionPill
              label={t("hub.all")}
              selected={!draft.classKey}
              onClick={() => setClass(null)}
            />
            {classes.map((cls) => (
              <OptionPill
                key={cls.key}
                label={pickLabel(cls.label, locale)}
                selected={draft.classKey === cls.key}
                onClick={() =>
                  setClass(draft.classKey === cls.key ? null : cls.key)
                }
              />
            ))}
          </div>
        );
      case "type":
        return (
          <div className="grid grid-cols-2 gap-2">
            {present.types.map((key) => (
              <Tile
                key={key}
                label={bikeTypeLabel(key, locale)}
                sub={t(`hub.typeSub.${key}`)}
                selected={draft.types.includes(key)}
                onClick={() => toggleType(key)}
              />
            ))}
          </div>
        );
      case "ride":
        return (
          <div className="grid grid-cols-2 gap-2">
            {present.rides.map((key) => (
              <Tile
                key={key}
                label={rideStyleLabel(key, locale)}
                sub={t(`hub.rideSub.${key}`)}
                selected={draft.rides.includes(key)}
                onClick={() => toggleRide(key)}
              />
            ))}
          </div>
        );
      case "trans":
        return (
          <div className="flex flex-wrap gap-2">
            {present.trans.map((key) => (
              <OptionPill
                key={key}
                label={t(key === "AT" ? "hub.at" : "hub.mt")}
                selected={draft.trans === key}
                onClick={() => setTrans(key)}
              />
            ))}
          </div>
        );
      case "party":
        // 제조사와 같은 2열 타일 — 라벨만("2인"), 보조 문구 없음. 데이터에 있는 인원만 오름차순.
        return (
          <div className="grid grid-cols-2 gap-2">
            {parties.map((key) => (
              <Tile
                key={key}
                label={partyLabel(key)}
                selected={draft.parties.includes(key)}
                onClick={() => toggleParty(key)}
              />
            ))}
          </div>
        );
    }
  };

  const renderPanel = (axis: AxisKey) => (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-5 pt-4 pb-3">
        <h2 className="text-base font-semibold text-gray-900">
          {titleOf(axis)}
        </h2>
        <button
          type="button"
          onClick={() => clearAxis(axis)}
          disabled={!draftActive(axis)}
          className="text-[13px] text-gray-500 hover:text-gray-900 disabled:opacity-40"
        >
          {t("hub.clearAxis")}
        </button>
      </div>
      <div className="px-5 pb-4">{renderBody(axis)}</div>
      <div className="px-5 pb-5 pt-1">
        <button
          type="button"
          onClick={applyDraft}
          className="h-12 w-full rounded-xl bg-orange-500 text-[15px] font-semibold text-white hover:bg-orange-600"
        >
          {t("hub.applyAxis")}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {visible.map((axis) => {
        const { label, extra } = summaryOf(axis);
        const active = isActive(axis);
        const pill = (
          <button
            type="button"
            onClick={() => openPicker(axis)}
            aria-expanded={openAxis === axis}
            className={cn(
              // 크기·글자·색은 바로 위 공용 필터 버튼(날짜·필터·카테고리)과 똑같이 —
              // 모바일 34px/13px, 데스크톱 40px/14px, 활성이면 굵게(09-23 사용자 결정).
              "inline-flex h-8.5 shrink-0 items-center gap-0.5 whitespace-nowrap rounded-full bg-white pl-3.5 pr-2.5 text-[13px] font-medium text-gray-700 transition hover:bg-gray-50",
              "mapShow:h-10 mapShow:pl-4 mapShow:pr-3 mapShow:text-sm",
              // ring 이 아니라 border — ring 은 box-shadow 라 박스 바깥에 그려져, 알약
              // 줄의 overflow-x-auto 가 줄 맨 앞 알약의 왼쪽 1px를 잘랐다(허브는
              // 카테고리 칩을 숨겨 이 알약이 줄의 시작이다, 09-23). border 는 박스
              // 안쪽이라 잘리지 않고, 같은 줄의 카테고리 칩과도 같은 방식이 된다.
              // 색은 바로 위 필터 버튼(날짜·필터·카테고리)과 같게 — 목록 헤더 gray-300,
              // 지도 위 black/10.
              floating
                ? `pointer-events-auto border border-black/10 ${FLOATING_SHADOW}`
                : "border border-gray-300",
              active && "font-semibold",
            )}
          >
            {active ? (
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" />
            ) : null}
            <span>{label}</span>
            {extra > 0 ? (
              <span className="rounded-full bg-gray-100 px-1.5 text-[11px] font-medium leading-4 text-gray-500">
                +{extra}
              </span>
            ) : null}
            <ChevronDown className="h-3.5 w-3.5 text-gray-400 mapShow:h-4 mapShow:w-4" />
          </button>
        );

        // sm 이상: 알약 아래 팝오버. 미만: 바텀 시트(정렬 다이얼로그와 같은 Drawer).
        if (isWide) {
          return (
            <Popover
              key={axis}
              open={openAxis === axis}
              onOpenChange={(next) => setOpenAxis(next ? axis : null)}
            >
              <PopoverTrigger asChild>{pill}</PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={8}
                className="w-[360px] rounded-2xl border-gray-100 bg-white p-0 shadow-lg"
              >
                {renderPanel(axis)}
              </PopoverContent>
            </Popover>
          );
        }

        return (
          <span key={axis} className="contents">
            {pill}
            <Drawer
              open={openAxis === axis}
              onOpenChange={(next) => setOpenAxis(next ? axis : null)}
            >
              <DrawerContent className="h-auto max-h-none bg-white p-0">
                <VisuallyHidden>
                  <DrawerTitle>{titleOf(axis)}</DrawerTitle>
                </VisuallyHidden>
                {renderPanel(axis)}
              </DrawerContent>
            </Drawer>
          </span>
        );
      })}
    </>
  );
}

/** 피커 안 옵션 알약 — 선택 = 진한 테두리(검정 채움 아님). */
function OptionPill({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "rounded-full px-3.5 py-2 text-[13px] whitespace-nowrap transition",
        selected
          ? "border-2 border-gray-900 bg-white font-semibold text-gray-900"
          : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50",
      )}
    >
      {label}
    </button>
  );
}

/** 타일 — 아이콘 자리(선택) + 라벨 + 한 줄 보조 문구(없으면 라벨만). 선택 = 진한 테두리. */
function Tile({
  label,
  sub,
  selected,
  onClick,
}: {
  label: string;
  sub?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex min-w-0 flex-col items-start gap-1 rounded-xl px-3 py-3 text-left transition",
        selected
          ? "border-2 border-gray-900 bg-white"
          : "border border-gray-200 bg-white hover:bg-gray-50",
      )}
    >
      <span
        className={cn(
          "text-[13px] text-gray-900",
          selected ? "font-semibold" : "font-medium",
        )}
      >
        {label}
      </span>
      {sub ? (
        <span className="text-[12px] leading-snug text-gray-500 break-keep">{sub}</span>
      ) : null}
    </button>
  );
}
